<template>
  <AdminLayout>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div class="mb-6 flex items-center gap-3">
        <div class="p-2 bg-[#22282F]/10 rounded-lg">
          <MessageSquare class="w-7 h-7 text-[#22282F]" />
        </div>
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-gray-900">Feedback</h1>
          <p class="text-sm text-gray-500">Suggestions anonymes reçues et configuration du support.</p>
        </div>
      </div>

      <!-- Onglets internes -->
      <div class="flex gap-1 border-b border-gray-200 mb-6">
        <button
          @click="activeTab = 'suggestions'"
          :class="['px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors',
            activeTab === 'suggestions' ? 'border-[#22282F] text-[#22282F]' : 'border-transparent text-gray-500 hover:text-gray-800']"
        >Suggestions</button>
        <button
          @click="activeTab = 'config'"
          :class="['px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors',
            activeTab === 'config' ? 'border-[#22282F] text-[#22282F]' : 'border-transparent text-gray-500 hover:text-gray-800']"
        >Configuration support</button>
      </div>

      <!-- ============ SUGGESTIONS ============ -->
      <section v-if="activeTab === 'suggestions'">
        <!-- Filtres -->
        <div class="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap items-end gap-3">
          <div>
            <label class="block text-xs font-semibold text-gray-600 mb-1">Profil (poste)</label>
            <input v-model="filters.profil" type="text" placeholder="ex. Chef de Service"
              class="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[180px]" @keyup.enter="applyFilters" />
          </div>
          <div>
            <label class="block text-xs font-semibold text-gray-600 mb-1">Catégorie</label>
            <select v-model="filters.categorie" class="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[160px]">
              <option value="">Toutes</option>
              <option v-for="c in categories" :key="c.value" :value="c.value">{{ c.label }}</option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-semibold text-gray-600 mb-1">Du</label>
            <input type="date" v-model="filters.from" class="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label class="block text-xs font-semibold text-gray-600 mb-1">Au</label>
            <input type="date" v-model="filters.to" class="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <button @click="applyFilters" class="px-4 py-2 bg-[#22282F] text-white rounded-lg text-sm hover:opacity-90">Filtrer</button>
          <button @click="resetFilters" class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50">Réinitialiser</button>
          <button @click="exportCsv" :disabled="exporting"
            class="ml-auto px-4 py-2 border border-emerald-600 text-emerald-700 rounded-lg text-sm hover:bg-emerald-50 flex items-center gap-2 disabled:opacity-50">
            <Download class="w-4 h-4" />{{ exporting ? 'Export…' : 'Exporter CSV' }}
          </button>
        </div>

        <div v-if="loading" class="py-16 text-center text-gray-400">Chargement…</div>
        <div v-else-if="error" class="py-16 text-center text-red-500">{{ error }}</div>

        <template v-else>
          <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-gray-50 text-left text-gray-500 text-xs uppercase tracking-wide">
                  <th class="px-4 py-3 font-semibold whitespace-nowrap">Date</th>
                  <th class="px-4 py-3 font-semibold whitespace-nowrap">Profil</th>
                  <th class="px-4 py-3 font-semibold whitespace-nowrap">Catégorie</th>
                  <th class="px-4 py-3 font-semibold">Message</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in items" :key="s.id" class="border-t border-gray-100 align-top">
                  <td class="px-4 py-3 text-gray-500 whitespace-nowrap">{{ formatDate(s.created_at) }}</td>
                  <td class="px-4 py-3 whitespace-nowrap">{{ s.profil_label }}</td>
                  <td class="px-4 py-3 whitespace-nowrap">
                    <span class="inline-block px-2 py-0.5 rounded-full text-xs bg-[#1d2a5e]/10 text-[#1d2a5e]">{{ s.categorie_label }}</span>
                  </td>
                  <td class="px-4 py-3 text-gray-700 whitespace-pre-line">{{ s.message }}</td>
                </tr>
                <tr v-if="items.length === 0">
                  <td colspan="4" class="px-4 py-10 text-center text-gray-400">Aucune suggestion.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="flex items-center justify-between mt-4 text-sm text-gray-500">
            <span>{{ count }} suggestion(s)</span>
            <div class="flex items-center gap-2">
              <button @click="prevPage" :disabled="page <= 1"
                class="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50">Précédent</button>
              <span>Page {{ page }} / {{ totalPages }}</span>
              <button @click="nextPage" :disabled="page >= totalPages"
                class="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50">Suivant</button>
            </div>
          </div>
        </template>
      </section>

      <!-- ============ CONFIGURATION ============ -->
      <section v-else class="max-w-2xl">
        <div v-if="configLoading" class="py-16 text-center text-gray-400">Chargement…</div>
        <form v-else @submit.prevent="saveConfig" class="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Lien de la réunion Teams</label>
            <input type="url" v-model="form.teams_url" placeholder="https://teams.microsoft.com/l/meetup-join/…"
              class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            <p class="text-xs text-gray-400 mt-1">Bouton « Parler à un technicien » du widget.</p>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Heure de début</label>
              <input type="time" v-model="form.horaire_debut" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Heure de fin</label>
              <input type="time" v-model="form.horaire_fin" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>

          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">Jours de disponibilité</label>
            <div class="flex flex-wrap gap-2">
              <label v-for="(j, idx) in joursLabels" :key="idx"
                :class="['px-3 py-1.5 rounded-lg border text-sm cursor-pointer select-none',
                  form.jours.includes(idx) ? 'border-[#22282F] bg-[#22282F]/5 text-[#22282F] font-semibold' : 'border-gray-300 text-gray-500']">
                <input type="checkbox" class="hidden" :value="idx" :checked="form.jours.includes(idx)" @change="toggleJour(idx)" />
                {{ j }}
              </label>
            </div>
          </div>

          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Fuseau horaire</label>
            <input type="text" v-model="form.timezone" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>

          <div class="flex items-center gap-3 pt-2">
            <button type="submit" :disabled="saving"
              class="px-5 py-2.5 bg-[#22282F] text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50">
              {{ saving ? 'Enregistrement…' : 'Enregistrer' }}
            </button>
            <span v-if="configSaved" class="text-emerald-600 text-sm flex items-center gap-1">
              <Check class="w-4 h-4" /> Enregistré
            </span>
            <span v-if="configError" class="text-red-500 text-sm">{{ configError }}</span>
          </div>
        </form>
      </section>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, reactive, onMounted } from 'vue'
import { MessageSquare, Download, Check } from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { apiFetch, API_BASE_URL } from '@/utils/apiFetch'

interface Choice { value: string; label: string }
interface Suggestion {
  id: number
  profil: string
  profil_label: string
  categorie: string
  categorie_label: string
  message: string
  created_at: string
}

const activeTab = ref<'suggestions' | 'config'>('suggestions')

const categories = ref<Choice[]>([])
const joursLabels = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']

// ---- Suggestions ----
const items = ref<Suggestion[]>([])
const count = ref(0)
const page = ref(1)
const pageSize = ref(25)
const loading = ref(false)
const error = ref<string | null>(null)
const exporting = ref(false)

const filters = reactive({ profil: '', categorie: '', from: '', to: '' })

const totalPages = computed(() => Math.max(1, Math.ceil(count.value / pageSize.value)))

function buildQuery(includePaging = true): string {
  const p = new URLSearchParams()
  if (filters.profil) p.set('profil', filters.profil)
  if (filters.categorie) p.set('categorie', filters.categorie)
  if (filters.from) p.set('from', filters.from)
  if (filters.to) p.set('to', filters.to)
  if (includePaging) {
    p.set('page', String(page.value))
    p.set('page_size', String(pageSize.value))
  }
  return p.toString()
}

async function fetchSuggestions() {
  loading.value = true
  error.value = null
  try {
    const data = await apiFetch<{ count: number; results: Suggestion[]; page: number; page_size: number }>(
      `/api/admin/feedback/suggestions/?${buildQuery()}`
    )
    items.value = data.results
    count.value = data.count
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Erreur de chargement.'
  } finally {
    loading.value = false
  }
}

function applyFilters() { page.value = 1; fetchSuggestions() }
function resetFilters() {
  filters.profil = ''; filters.categorie = ''; filters.from = ''; filters.to = ''
  page.value = 1; fetchSuggestions()
}
function nextPage() { if (page.value < totalPages.value) { page.value++; fetchSuggestions() } }
function prevPage() { if (page.value > 1) { page.value--; fetchSuggestions() } }

async function exportCsv() {
  exporting.value = true
  try {
    const res = await fetch(`${API_BASE_URL}/api/admin/feedback/suggestions/export/?${buildQuery(false)}`, {
      credentials: 'include',
    })
    if (!res.ok) throw new Error(`Erreur ${res.status}`)
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `suggestions-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } catch (e) {
    error.value = e instanceof Error ? e.message : "Échec de l'export."
  } finally {
    exporting.value = false
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// ---- Configuration ----
const configLoading = ref(false)
const saving = ref(false)
const configSaved = ref(false)
const configError = ref<string | null>(null)
const form = reactive<{ teams_url: string; horaire_debut: string; horaire_fin: string; jours: number[]; timezone: string }>({
  teams_url: '', horaire_debut: '08:30', horaire_fin: '18:00', jours: [0, 1, 2, 3, 4], timezone: 'Africa/Porto-Novo',
})

function toggleJour(idx: number) {
  const i = form.jours.indexOf(idx)
  if (i >= 0) form.jours.splice(i, 1)
  else form.jours.push(idx)
}

async function fetchConfig() {
  configLoading.value = true
  try {
    const data = await apiFetch<any>('/api/admin/feedback/config/')
    form.teams_url = data.teams_url || ''
    form.horaire_debut = data.horaire_debut || '08:30'
    form.horaire_fin = data.horaire_fin || '18:00'
    form.jours = Array.isArray(data.jours) ? data.jours : [0, 1, 2, 3, 4]
    form.timezone = data.timezone || 'Africa/Porto-Novo'
    if (Array.isArray(data.categories)) categories.value = data.categories
  } catch (e) {
    configError.value = e instanceof Error ? e.message : 'Erreur de chargement.'
  } finally {
    configLoading.value = false
  }
}

async function saveConfig() {
  saving.value = true
  configSaved.value = false
  configError.value = null
  try {
    await apiFetch('/api/admin/feedback/config/', {
      method: 'PUT',
      body: JSON.stringify({
        teams_url: form.teams_url,
        horaire_debut: form.horaire_debut,
        horaire_fin: form.horaire_fin,
        jours: [...form.jours].sort((a, b) => a - b),
        timezone: form.timezone,
      }),
    })
    configSaved.value = true
    setTimeout(() => { configSaved.value = false }, 2500)
  } catch (e) {
    configError.value = e instanceof Error ? e.message : "Échec de l'enregistrement."
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  fetchConfig()      // charge aussi les listes profils/catégories pour les filtres
  fetchSuggestions()
})
</script>
